import React, { useState, useEffect } from 'react';
import { useTheme, isValidTheme } from '../components/common/ThemeContext';
import { useToaster } from '../components/common/Toaster';
import { Settings as SettingsIcon, Palette, Bell, Type, Table2 } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardIconHeader,
} from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';

interface Preferences {
  compactTables: boolean;
  largerText: boolean;
  notifications: boolean;
}

const defaultPreferences: Preferences = {
  compactTables: false,
  largerText: false,
  notifications: true,
};

const getStoredPreferences = (): Preferences => {
  try {
    const stored = localStorage.getItem('user-preferences');
    return stored ? { ...defaultPreferences, ...JSON.parse(stored) } : defaultPreferences;
  } catch {
    return defaultPreferences;
  }
};

const Settings: React.FC = () => {
  const { theme, setTheme } = useTheme();
  const { notify } = useToaster();
  const [preferences, setPreferences] = useState<Preferences>(getStoredPreferences);

  useEffect(() => {
    localStorage.setItem('user-preferences', JSON.stringify(preferences));

    // Apply larger text setting
    document.documentElement.classList.toggle('larger-text', preferences.largerText);

    // Apply compact tables setting
    document.documentElement.classList.toggle('compact-tables', preferences.compactTables);
  }, [preferences]);

  // Apply preferences on mount
  useEffect(() => {
    document.documentElement.classList.toggle('larger-text', preferences.largerText);
    document.documentElement.classList.toggle('compact-tables', preferences.compactTables);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onThemeChange = (value: string) => {
    if (isValidTheme(value)) {
      setTheme(value);
      notify('Theme updated', `Theme set to ${value}`, 'success');
    }
  };

  const updatePreference = (key: keyof Preferences, value: boolean) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
    const label = key === 'compactTables' ? 'Compact tables' :
                  key === 'largerText' ? 'Larger text' : 'Notifications';
    notify('Preference updated', `${label} ${value ? 'enabled' : 'disabled'}`, 'success');
  };

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <Card>
        <CardIconHeader icon={<SettingsIcon size={22} className="text-primary" />}>
          <CardTitle>Settings</CardTitle>
          <CardDescription>
            Customize your application experience. Changes are saved automatically
            and persist across sessions.
          </CardDescription>
        </CardIconHeader>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Appearance Card */}
        <Card className="animate-fade-in-up">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Palette size={18} className="text-muted-foreground" />
              <CardTitle className="text-lg">Appearance</CardTitle>
            </div>
            <CardDescription>
              Customize how the application looks.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="theme-select">Color Theme</Label>
                <Select value={theme} onValueChange={onThemeChange}>
                  <SelectTrigger id="theme-select" className="w-full">
                    <SelectValue placeholder="Select theme" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system">System (auto)</SelectItem>
                    <SelectItem value="light">Light</SelectItem>
                    <SelectItem value="dark">Dark</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Choose your preferred color scheme. System will follow your device settings.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Preferences Card */}
        <Card className="animate-fade-in-up" style={{ animationDelay: '100ms' }}>
          <CardHeader>
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-muted-foreground" />
              <CardTitle className="text-lg">Preferences</CardTitle>
            </div>
            <CardDescription>
              Adjust behavior and accessibility options.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 stagger-children">
              {/* Compact Tables */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors animate-fade-in">
                <div className="flex-1">
                  <label htmlFor="pref-density" className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <Table2 size={16} className="text-muted-foreground" />
                    Compact tables
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Reduces row padding for denser data views.
                  </p>
                </div>
                <Switch
                  id="pref-density"
                  checked={preferences.compactTables}
                  onCheckedChange={(checked) => updatePreference('compactTables', checked)}
                />
              </div>

              {/* Larger Text */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors animate-fade-in">
                <div className="flex-1">
                  <label htmlFor="pref-large-text" className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <Type size={16} className="text-muted-foreground" />
                    Larger text
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Increases base font size for readability.
                  </p>
                </div>
                <Switch
                  id="pref-large-text"
                  checked={preferences.largerText}
                  onCheckedChange={(checked) => updatePreference('largerText', checked)}
                />
              </div>

              {/* Notifications */}
              <div className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors animate-fade-in">
                <div className="flex-1">
                  <label htmlFor="pref-notify" className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                    <Bell size={16} className="text-muted-foreground" />
                    Enable notifications
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Show toast alerts for key actions.
                  </p>
                </div>
                <Switch
                  id="pref-notify"
                  checked={preferences.notifications}
                  onCheckedChange={(checked) => updatePreference('notifications', checked)}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Settings;
